/**
 * **Export to Excel** (`specs/M1-B9-export-templates.md` §7, captured geometry in
 * `discovery/ui-reference/export/INDEX.md` §1).
 *
 * monday's modal is **520 × 387** and offers exactly three controls: `Updates` (off),
 * `Subitems` (**on**) and an `Email me a copy` toggle (off), plus a fixed info banner and the
 * `Export` button. There is no column picker and no format choice — the dialog was captured
 * before this was built, and the two things the brief guessed at are simply not there.
 *
 * A small board comes back as bytes and is saved immediately; a large one comes back as a job
 * id and the dialog polls until it has a link.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { formatMondayNumber, useOverlayDismiss, useToast } from '@elizmonday/ui';
import { useTRPC, useTRPCClient } from '../../lib/trpc';
import './export.css';

/** Turns the base64 payload into a download without ever routing it through the server. */
function saveBlob(base64: string, filename: string): void {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  const url = URL.createObjectURL(
    new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
  );
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Revoking synchronously cancels the download in Firefox; one tick is enough.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function ExportDialog({ boardId, onClose }: { boardId: string; onClose(): void }) {
  const trpc = useTRPC();
  const client = useTRPCClient();
  const toast = useToast();

  /* P-013: Escape closes, and focus goes back to the `More actions` row that opened it (P-223). */
  useOverlayDismiss({ onClose });

  const [includeUpdates, setIncludeUpdates] = useState(false);
  const [includeSubitems, setIncludeSubitems] = useState(true);
  const [emailMeACopy, setEmailMeACopy] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);

  const plan = useQuery({ ...trpc.export.plan.queryOptions({ boardId }), staleTime: 30_000 });

  const run = useMutation({
    mutationFn: () => client.export.board.mutate({ boardId, includeUpdates, includeSubitems, emailMeACopy }),
    onSuccess: (result) => {
      if (result.kind === 'sync' && result.base64 !== null && result.filename !== null) {
        saveBlob(result.base64, result.filename);
        toast('Export ready', { kind: 'positive' });
        onClose();
        return;
      }
      setJobId(result.jobId);
    },
    onError: (error: unknown) => {
      toast(error instanceof Error ? error.message : 'The export failed', { kind: 'negative' });
    },
  });

  // The poll only exists while a job is running; `refetchInterval` stops itself on COMPLETED.
  const job = useQuery({
    ...trpc.export.jobStatus.queryOptions({ jobId: jobId ?? '' }),
    enabled: jobId !== null,
    refetchInterval: (query) => (query.state.data?.status === 'RUNNING' ? 1500 : false),
  });

  useEffect(() => {
    if (job.data?.status === 'COMPLETED' && job.data.downloadUrl !== null) {
      window.location.href = job.data.downloadUrl;
      toast('Export ready', { kind: 'positive' });
      onClose();
    }
    if (job.data?.status === 'FAILED') {
      toast(job.data.failureMessage ?? 'The export failed', { kind: 'negative' });
      setJobId(null);
    }
  }, [job.data, onClose, toast]);

  const busy = run.isPending || jobId !== null;

  return (
    <div className="ez-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="ez-export-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Export to Excel"
        data-testid="export-dialog"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="ez-export-dialog__head">
          <h2>Export to Excel</h2>
          <button type="button" aria-label="Close" data-testid="export-close" onClick={onClose}>
            ✕
          </button>
        </header>

        <p className="ez-export-dialog__section">What to include?</p>
        <label className="ez-export-dialog__check">
          <input
            type="checkbox"
            data-testid="export-updates"
            checked={includeUpdates}
            onChange={(event) => setIncludeUpdates(event.target.checked)}
          />
          <span>Updates</span>
        </label>
        <label className="ez-export-dialog__check">
          <input
            type="checkbox"
            data-testid="export-subitems"
            checked={includeSubitems}
            onChange={(event) => setIncludeSubitems(event.target.checked)}
          />
          <span>Subitems</span>
        </label>

        <div className="ez-export-dialog__email">
          <div>
            <strong>Email me a copy</strong>
            <em>The download link remains active for 60 minutes.</em>
          </div>
          {/* Rendered, never hidden: SMTP is not configured on this deployment yet, so the
              control says so instead of silently doing nothing (M1-B9 §7). */}
          <span className="ez-export-dialog__toggle">
            <span>No</span>
            <button
              type="button"
              role="switch"
              aria-checked={emailMeACopy}
              data-testid="export-email"
              disabled
              title="Email delivery needs SMTP, which is not configured on this deployment"
              onClick={() => setEmailMeACopy((value) => !value)}
            />
            <span>Yes</span>
          </span>
        </div>

        <p className="ez-export-dialog__note" data-testid="export-note">
          Your export will include applied Filters. Other display settings (e.g Sort, Group by..)
          are not supported.
        </p>

        {plan.data?.async === true ? (
          <p className="ez-export-dialog__async" data-testid="export-async-hint">
            This board has {formatMondayNumber(plan.data.items)} items, so the file is built in the
            background and the download starts when it is ready.
          </p>
        ) : null}

        <footer className="ez-export-dialog__foot">
          <button
            type="button"
            className="ez-export-dialog__submit"
            data-testid="export-submit"
            disabled={busy}
            onClick={() => run.mutate()}
          >
            {busy ? 'Exporting…' : 'Export'}
          </button>
        </footer>
      </div>
    </div>
  );
}
